import React from 'react';
import {
    Responsive,
    SimpleList,
    Datagrid,
    TextField,
    SimpleForm,
    TextInput,
    NumberInput,
    required,
    number,
    minValue
} from 'admin-on-rest';
import List from '../admin/List';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import EditButton from '../buttons/EditButton';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/image/collections-bookmark';
import {resolveAccess} from '../shared/authFunctions';

export const fuseLoyaltyRankIcon = Icon;

const styles = {
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
    fieldWide: {minWidth: '50%'}
};

/*
 * View: List
 */

// Set title for List Loyalty Ranks
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Loyalty Ranks</span>;

const ListDescription = () => {
    return (
        <span>
            <p>Loyalty Ranks set levels for Loyalty Accounts to reach, offering reward points and earning ratios that
                can increase with purchases.</p>
            <p>Ranks work with Incentive Tiers to provide extra points for customers. Ranks and Tiers must exist before
                Loyalty Accounts may be created.</p>
        </span>
    );
};

export const fuseLoyaltyRankList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'rank_point_min', order: 'ASC'}}>
        {permissions =>
            <Responsive
                small={
                    <SimpleList
                        primaryText={record => record.name}
                    />
                }
                medium={
                    <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                        <TextField source="name" label="Rank Name"/>
                        <TextField source="rank_point_min" label="Minimum Rank"/>
                        <TextField source="reward_point_min" label="Minimum Reward"/>
                        <TextField source="rank_point_value" label="Rank Value"/>
                        {resolveAccess(permissions, ['loyalty_all']) &&
                        <EditButton/>
                        }
                    </Datagrid>
                }
            />
        }
    </List>
);

/*
 * View: Create form
 */

// Set title for Create Loyalty Rank
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create a Loyalty Rank</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Loyalty Ranks set levels for Loyalty Accounts to reach, offering reward points and earning ratios that
                can increase with purchases.</p>
            <p>Create a Loyalty Rank by filling the fields below.</p>
        </span>
    );
};
// Set defaults for Create form
const defaultCreateValues = {
    rank_point_min: 0,
    rank_point_days_valid: 400,
    rank_point_value: 1,
    reward_point_min: 1,
    reward_point_days_valid: 400,
    reward_point_earning_ratio: 0,
    reward_point_multiplier: 0,
    reward_point_value: 0,
    bonus_reward_points: 0
};

/*
 * Create Loyalty Rank form
 */
export const fuseLoyaltyRankCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} redirect="list">
            <TextInput source="name" label="Name of Rank" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                Enter a <strong>Display Name</strong> for this Loyalty Rank.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Name</strong> will be shown to the Customer when viewing what Rank they have reached.
                    Names should indicate an increase in value, e.g., "Silver Level", "Gold Level", "Superstar".
                </ReactTooltip>
            </span>

            <TextInput source="description" label="Description of Rank"
                       style={styles.fieldWide} elStyle={styles.fieldWide}/>
            <span style={viewStyles.fieldDescription}>Enter a <strong>Description</strong> of this Loyalty Rank.</span>

            <span style={viewStyles.fieldHeader}>Loyalty Rank</span>

            <NumberInput source="rank_point_min" label="Rank Point Minimum" step={1}
                         validate={[required, number, minValue(0, 'Must be a positive number.')]}/>
            <span style={viewStyles.fieldDescription}>
                Set the <strong>Rank Point Minimum</strong> to earn this Loyalty Rank.
                <br/><br/>
                <strong>Note:</strong> If this is your lowest Rank, this value must be 0.
                <TooltipIcon dataFor="ttRankPointMin"/>
                <ReactTooltip id="ttRankPointMin" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Rank Point Minimum</strong> represents the minimum Reward Points a Customer must earn
                    through purchases to achieve this Rank. It may be considered the "level" of the Rank.
                    <br/><br/>
                    For instance, a Rank Point Minimum might be set to 500. Customers that earn 500 Reward Points are
                    now considered this Rank, which provides benefits such as increased earning ratio, bonuses, etc.
                    <br/><br/>
                    Your lowest Rank should have a minimum of 0, to accommodate all Customers.
                </ReactTooltip>
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <NumberInput source="rank_point_value" label="Rank Point Value" step={0.5}
                         validate={[required, number, minValue(1, 'Must be at least 1.')]}/>
            <span style={viewStyles.fieldDescription}>
                Set a <strong>Rank Point Value</strong> (at least 1).
                <TooltipIcon dataFor="ttRankPointValue"/>
                <ReactTooltip id="ttRankPointValue" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Rank Point Value</strong> sets the Reward Point level for dollars spent. Typically
                    every dollar spent equals a single Reward Point. Higher Ranks may have higher values.
                    <br/><br/>
                    For instance, a Value of 1.5 will earn 1.5 Reward Points for every dollar spent.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldHeader}>Reward Points</span>

            <NumberInput source="reward_point_min" label="Reward Point Minimum" step={1}
                         validate={[required, number, minValue(0, 'Must be a positive number.')]}/>
            <span style={viewStyles.fieldDescription}>
                Set the <strong>Reward Point Minimum</strong> before a Customer may spend Reward Points.
                <TooltipIcon dataFor="ttRewardPointMin"/>
                <ReactTooltip id="ttRewardPointMin" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward Point</strong> sets the minimum total of Reward Points a Customer must earn
                    before becoming eligible to spend Reward Points for a purchase on the POS System. Before this total,
                    a Customer cannot use Reward Points during a purchase.
                    <br/><br/>
                    For instance, a Rank might have a Reward Point Minimum of 250. A Customer who spends two hundred
                    dollars cannot use their Reward Points unless they purchase the minimum.
                </ReactTooltip>
            </span>

            <NumberInput source="reward_point_days_valid" label="Reward/Rank Point Days Valid" step={1}
                         validate={[required, number, minValue(1, 'Must be a positive number.')]}/>
            <span style={viewStyles.fieldDescription}>
                Total of <strong>Days</strong> the Reward/Rank Points may remain <strong>Valid</strong>.
                <TooltipIcon dataFor="ttRewardPointDaysValid"/>
                <ReactTooltip id="ttRewardPointDaysValid" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward/Rank Point Days Valid</strong> total is the number of days a Customer's Points
                    may remain unspent. After this total passes, unspent Reward Points will be reset to 0.
                    <br/><br/>
                    The default is 400 days to provide for a comfortable buffer for Customers shopping at a seasonal
                    Store, such as for an annual holiday.
                </ReactTooltip>
            </span>

            <NumberInput source="reward_point_value" label="Reward Point Value" step={0.5}
                         validate={[required, number, minValue(0, 'Must be at least 0.')]}/>
            <span style={viewStyles.fieldDescription}>
                Set a <strong>Reward Point Cash Value</strong>.
                <TooltipIcon dataFor="ttRewardPointValue"/>
                <ReactTooltip id="ttRewardPointValue" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward Point Value</strong> indicates the cash value of Reward Points. You may choose
                    to keep this number at 0 if you do not wish Reward Points to be converted to cash.
                    <br/><br/>
                    For example, a Value of 0.1 means that 1 Reward Point is worth 10 cents.
                </ReactTooltip>
            </span>

            <TextInput source="reward_point_earning_ratio" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>
            <TextInput source="reward_point_multiplier" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>
            <TextInput source="bonus_reward_points" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the list of Loyalty Ranks.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Loyalty Rank
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'Loyalty Rank'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify fields below. Click <strong>Save</strong> to store this Loyalty Rank.</p>
        </span>
    );
};

/*
 * Edit Loyalty Rank form
 */
export const fuseLoyaltyRankEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm>
            <TextInput source="name" label="Name of Rank" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Display Name</strong> for this Loyalty Rank.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Name</strong> is shown to the Customer when viewing what Rank they have reached.
                </ReactTooltip>
            </span>

            <TextInput source="description" label="Description of Rank"
                       style={styles.fieldWide} elStyle={styles.fieldWide}/>
            <span style={viewStyles.fieldDescription}>The <strong>Description</strong> of this Loyalty Rank.</span>

            <span style={viewStyles.fieldHeader}>Loyalty Rank</span>

            <NumberInput source="rank_point_min" label="Rank Point Minimum" step={1}
                         validate={[required, number, minValue(0, 'Must be a positive number.')]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Rank Point Minimum</strong> to earn this Loyalty Rank.
                <br/><br/>
                <strong>Note:</strong> Your lowest Rank must have a value of 0.
                <TooltipIcon dataFor="ttRankPointMin"/>
                <ReactTooltip id="ttRankPointMin" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Rank Point Minimum</strong> represents the minimum Reward Points a Customer must earn
                    through purchases to achieve this Rank. It may be considered the "level" of the Rank.
                    <br/><br/>
                    For instance, a Rank Point Minimum might be set to 500. Customers that earn 500 Reward Points are
                    now considered this Rank, which provides benefits such as increased earning ratio, bonuses, etc.
                    <br/><br/>
                    Your lowest Rank should have a minimum of 0, to accommodate all Customers.
                </ReactTooltip>
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <NumberInput source="rank_point_value" label="Rank Point Value" step={0.5}
                         validate={[required, number, minValue(1, 'Must be at least 1.')]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Rank Point Value</strong> of Reward Points to dollars.
                <TooltipIcon dataFor="ttRankPointValue"/>
                <ReactTooltip id="ttRankPointValue" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Rank Point Value</strong> sets the Reward Point level for dollars spent. Typically
                    every dollar spent equals a single Reward Point. Higher Ranks may have higher values.
                    <br/><br/>
                    For instance, a Value of 1.5 will earn 1.5 Reward Points for every dollar spent.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldHeader}>Reward Points</span>

            <NumberInput source="reward_point_min" label="Reward Point Minimum" step={1}
                         validate={[required, number, minValue(0, 'Must be a positive number.')]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Reward Point Minimum</strong> before a Customer may spend Reward Points.
                <TooltipIcon dataFor="ttRewardPointMin"/>
                <ReactTooltip id="ttRewardPointMin" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward Point</strong> sets the minimum total of Reward Points a Customer must earn
                    before becoming eligible to spend Reward Points for a purchase on the POS System. Before this total,
                    a Customer cannot use Reward Points during a purchase.
                    <br/><br/>
                    For instance, a Rank might have a Reward Point Minimum of 250. A Customer who spends two hundred
                    dollars cannot use their Reward Points unless they purchase the minimum.
                </ReactTooltip>
            </span>

            <NumberInput source="reward_point_days_valid" label="Reward/Rank Point Days Valid" step={1}
                         validate={[required, number, minValue(1, 'Must be a positive number.')]}/>
            <span style={viewStyles.fieldDescription}>
                Total of <strong>Days</strong> the Reward/Rank Points may remain <strong>Valid</strong>.
                <TooltipIcon dataFor="ttRewardPointDaysValid"/>
                <ReactTooltip id="ttRewardPointDaysValid" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward/Rank Point Days Valid</strong> total is the number of days a Customer's Points
                    may remain unspent. After this total passes, unspent Reward Points will be reset to 0.
                    <br/><br/>
                    The default is 400 days to provide for a comfortable buffer for Customers shopping at a seasonal
                    Store, such as for an annual holiday.
                </ReactTooltip>
            </span>

            <NumberInput source="reward_point_value" label="Reward Point Value" step={0.5}
                         validate={[required, number, minValue(0, 'Must be at least 0.')]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Reward Point Cash Value</strong>.
                <TooltipIcon dataFor="ttRewardPointValue"/>
                <ReactTooltip id="ttRewardPointValue" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward Point Value</strong> indicates the cash value of Reward Points. You may choose
                    to keep this number at 0 if you do not wish Reward Points to be converted to cash.
                    <br/><br/>
                    For example, a Value of 0.1 means that 1 Reward Point is worth 10 cents.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the list of Loyalty Ranks.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Loyalty/rank.js